import '../styles/login.css';
import { SmartyLogo } from '@/components/brand/SmartyLogo';
import { MallaNeuronal } from '@/components/brand/MallaNeuronal';
import { injectFontLink } from '@/components/themes/fontLoader';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';
import { isAdminDomain } from '@/lib/domain';
import { buildUrl } from '@/lib/url-utils';
import { getWebHost } from '@/config/app-domains.config';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recuperando, setRecuperando] = useState(false);
  const [verClave, setVerClave] = useState(false);
  const navigate = useNavigate();

  // La letra de smarty.lat (Poppins), con el mismo cargador que usa el editor de temas.
  useEffect(() => injectFontLink('poppins'), []);

  /**
   * Recuperar la contraseña.
   *
   * 🚨 ESTO NO EXISTÍA, Y DEJABA GENTE AFUERA SIN SALIDA. Busqué en todo el panel:
   *    `resetPasswordForEmail` no se llamaba desde ningún lado del acceso. Quien olvidaba su clave
   *    —o quien nunca la tuvo, porque el registro viejo se la inventaba y no se la mostraba— no
   *    tenía ninguna forma de volver a entrar. Al founder le pasó y hubo que generarle el enlace a
   *    mano desde el servidor.
   *
   * 🪤 EL DESTINO ES `/registro?recuperar=1` Y NO UNA PANTALLA NUEVA. Ahí ya vive el paso que pide
   *    una contraseña: hay sesión, falta clave. Hacer una segunda pantalla para escribir lo mismo
   *    sería mantener dos veces algo que tiene que decir exactamente lo mismo.
   *
   * 🪤 Y RESPONDE IGUAL EXISTA O NO EL CORREO. Un mensaje distinto para cada caso le diría a
   *    cualquiera qué correos tienen cuenta acá.
   */
  const recuperarClave = async () => {
    const correo = email.trim();
    if (!correo) {
      setError('Escribe tu correo arriba y vuelve a tocar «¿Olvidaste tu contraseña?».');
      return;
    }
    setError(null);
    setRecuperando(true);
    try {
      await supabase.auth.resetPasswordForEmail(correo, {
        redirectTo: buildUrl(getWebHost(), '/registro?recuperar=1'),
      });
      toast.success('Revisa tu correo', {
        description: `Si ${correo} tiene una cuenta, te llega un enlace para elegir una contraseña nueva.`,
      });
    } catch (err) {
      devLog.error('Error pidiendo recuperación:', err);
      toast.error('No se pudo enviar el correo', {
        description: 'Inténtalo de nuevo en un momento.',
      });
    } finally {
      setRecuperando(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      if (data.user) {
        toast.success('Sesión iniciada', {
          description: `Bienvenido, ${data.user.email}`,
        });

        const onAdminPath = window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/');
        if (isAdminDomain() || onAdminPath) {
          window.location.href = '/admin';
          return;
        }
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      devLog.error('❌ Error al iniciar sesión:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Error al iniciar sesión. Verifica tus credenciales.';
      setError(errorMessage);
      toast.error('Error al iniciar sesión', {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__wrap">
        {/*
          * ⭐ EL LADO IZQUIERDO ES EL INICIO DE SMARTY.LAT (founder, 21-oct: «que vaya de la mano con la página
          *    de inicio»): la malla, la etiqueta, el titular y la consola del Empleado de Ventas trabajando. Antes
          *    era una foto de restaurante de Unsplash con «POS & Restaurant Management», de cuando Smarty era otra
          *    cosa. En el celular no va: ahí manda el formulario.
          */}
        <aside className="login-page__marca" aria-hidden="true">
          <MallaNeuronal className="login-page__malla" />
          <a href="/" className="login-page__brand" tabIndex={-1}>
            <SmartyLogo size={26} className="login-page__isotipo" />
            Smarty
          </a>
          <div className="login-page__hero">
            <span className="login-page__etiqueta">Plataforma de operación con IA</span>
            <p className="login-page__titular">
              Tu negocio,
              <br />
              <em>en piloto automático.</em>
            </p>
            <p className="login-page__bajada">Ventas, pedidos, chats y tus empleados de IA, en un solo lugar.</p>
            <ConsolaDelAgente />
          </div>
          <p className="login-page__pie">© Smarty · smarty.lat</p>
        </aside>

        <main className="login-page__lado">
          <div className="login-page__inner">
            <a href="/" className="login-page__brand login-page__brand--movil">
              <SmartyLogo size={24} className="login-page__isotipo" />
              Smarty
            </a>

            <h1 className="login-page__title">Entra a tu panel</h1>
            <p className="login-page__subtitle">Usa el correo y la contraseña de tu cuenta.</p>

            <form onSubmit={handleLogin} className="login-page__form" data-allow-submit="true">
              {error && (
                <div className="login-page__error" role="alert">
                  <svg
                    className="login-page__error-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <div className="login-page__field">
                <label htmlFor="email" className="login-page__label">
                  Correo
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="nombre@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="email"
                  className="login-page__input"
                />
              </div>

              <div className="login-page__field">
                {/* El enlace va en la fila de la etiqueta, donde se lo busca: debajo del campo competía con el botón. */}
                <div className="login-page__label-fila">
                  <label htmlFor="password" className="login-page__label">
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => void recuperarClave()}
                    disabled={recuperando || loading}
                    className="login-page__link"
                  >
                    {recuperando ? 'Enviando…' : '¿Olvidaste tu contraseña?'}
                  </button>
                </div>
                <div className="login-page__clave">
                  <input
                    id="password"
                    type={verClave ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                    className="login-page__input"
                  />
                  <button
                    type="button"
                    className="login-page__ojo"
                    onClick={() => setVerClave((v) => !v)}
                    aria-label={verClave ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                    aria-pressed={verClave}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
                      <circle cx="12" cy="12" r="3" />
                      {verClave ? <path d="M4 4l16 16" /> : null}
                    </svg>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="login-page__submit"
              >
                {loading ? (
                  <>
                    <span className="login-page__spinner" aria-hidden />
                    Entrando…
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>

            {/*
              * 🚨 SIN «¿No tienes cuenta? Crea la tuya» (21-sep-2026). Ese enlace llevaba a
              *    `/registro` sin plan y abría una TIENDA en `basic`: un desconocido lo hizo y el
              *    founder tuvo que pasarlo a Drop a mano. Las tiendas se contratan hablando; el
              *    dropshipper llega por su propio enlace (`/registro?plan=drop`). El corte de
              *    verdad está en `create-store`; esto es no ofrecer la puerta.
              */}
            <p className="login-page__ayuda">
              ¿Problemas para entrar? Escríbenos a <a href="mailto:hola@smarty.lat">hola@smarty.lat</a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

/*
 * La consola del Empleado de Ventas, la del inicio de smarty.lat: las líneas aparecen una por una, como si el
 * agente estuviera trabajando, con una pausa más larga después de llamar una herramienta. Corre una sola vez
 * (en bucle distraería a quien está escribiendo su contraseña) y con «reducir movimiento» se muestra entera.
 */
const GUION: ReadonlyArray<{ marca: string; tipo: 'entra' | 'herramienta' | 'listo'; herramienta?: string; texto: ReactNode }> = [
  { marca: 'in', tipo: 'entra', texto: <>Nuevo mensaje en <b>WhatsApp</b> · pedido entrante</> },
  { marca: '›', tipo: 'entra', texto: '«Hola, quiero 1 Magnesium Complex para Surco»' },
  { marca: '⛭', tipo: 'herramienta', herramienta: 'verificar_stock', texto: 'consultando inventario…' },
  { marca: '✓', tipo: 'listo', texto: <>Stock disponible · <b>S/ 149.00</b></> },
  { marca: '⛭', tipo: 'herramienta', herramienta: 'crear_pedido', texto: 'registrando OP-2041, zona Surco…' },
  { marca: '✓', tipo: 'listo', texto: <>Pedido <b>confirmado</b> · pago contra entrega listo</> },
];

function ConsolaDelAgente() {
  const [visibles, setVisibles] = useState(() =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? GUION.length : 0,
  );

  useEffect(() => {
    if (visibles >= GUION.length) return;
    const anterior = GUION[visibles - 1];
    const espera = visibles === 0 ? 700 : anterior?.tipo === 'herramienta' ? 780 : 620;
    const t = setTimeout(() => setVisibles((n) => n + 1), espera);
    return () => clearTimeout(t);
  }, [visibles]);

  return (
    <div className="login-page__consola">
      <div className="login-page__consola-cabeza">
        <span className="login-page__consola-avatar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="8" width="16" height="12" rx="1" />
            <path d="M12 8V4M9 14h.01M15 14h.01M2 13h2M20 13h2" />
          </svg>
        </span>
        <span className="login-page__consola-nombre">
          Empleado de Ventas · IA<span>Trabajando · pedido OP-2041</span>
        </span>
        <span className="login-page__consola-estado">{visibles < GUION.length ? 'Razonando' : 'Listo'}</span>
      </div>
      <div className="login-page__consola-cuerpo">
        {GUION.slice(0, visibles).map((l, i) => (
          <div key={i} className={`login-page__linea login-page__linea--${l.tipo}`}>
            <span className="login-page__linea-marca">{l.marca}</span>
            <span>
              {l.herramienta ? <span className="login-page__herramienta">{l.herramienta}</span> : null}
              {l.texto}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
